/**
 * Microphone capture.
 *
 * Taps raw PCM through an AudioWorklet (never a deprecated ScriptProcessor),
 * resamples the device rate down to 16 kHz mono — the point every ASR backend
 * is tuned for — and applies a client-side VAD so the server is not spending
 * neurons on silence.
 *
 * The resampler is a linear interpolator with a small carry buffer. It is not
 * a windowed-sinc filter, and it does not need to be: at 48 kHz to 16 kHz with
 * speech-band content and Whisper-class models, the difference is inaudible in
 * the transcript. The worklet already low-passes implicitly by decimation.
 *
 * The alternative — `MediaStreamTrackProcessor` — is not used because it is not
 * available in Firefox and Safari at the time of writing, and this app has to
 * work everywhere.
 */

import { AUDIO } from "@shared/constants";

/** The worklet source, served from a Blob so there is no extra build step. */
const WORKLET_SOURCE = `
class DreamTongueCapture extends AudioWorkletProcessor {
  constructor() {
    super();
    this._buffer = new Float32Array(4096);
    this._offset = 0;
  }

  process(inputs) {
    const input = inputs[0];
    if (!input || input.length === 0) return true;

    const channel = input[0];
    if (!channel) return true;

    for (let i = 0; i < channel.length; i += 1) {
      if (this._offset >= this._buffer.length) {
        this.port.postMessage(this._buffer.slice(0));
        this._offset = 0;
      }
      this._buffer[this._offset] = channel[i];
      this._offset += 1;
    }
    return true;
  }
}

registerProcessor('dream-tongue-capture', DreamTongueCapture);
`;

export type CaptureCallbacks = {
  /** Raw mono PCM at the device sample rate. */
  onFrame: (frame: Float32Array, level: number) => void;
  onError: (error: Error) => void;
  onEnded: () => void;
};

export type CaptureOptions = {
  /** Device input device id. `undefined` uses the system default. */
  deviceId?: string;
  /**
   * Preferred input sample rate. Some drivers refuse non-standard rates; the
   * capture layer falls back to whatever the device gives us.
   */
  sampleRate?: number;
  echoCancellation?: boolean;
  noiseSuppression?: boolean;
  autoGainControl?: boolean;
};

export type CaptureStats = {
  readonly deviceSampleRate: number;
  readonly outputSampleRate: number;
  readonly ratio: number;
  readonly deviceLabel: string;
};

export class MicrophoneCapture {
  #context: AudioContext | null = null;
  #stream: MediaStream | null = null;
  #source: MediaStreamAudioSourceNode | null = null;
  #worklet: AudioWorkletNode | null = null;
  #callbacks: CaptureCallbacks;
  #options: CaptureOptions;
  #active = false;
  #stats: CaptureStats | null = null;

  /** Carry between input frames so resampling never drops a boundary sample. */
  #carry: Float32Array = new Float32Array(0);
  #resamplePhase = 0;
  #level = 0;

  constructor(callbacks: CaptureCallbacks, options: CaptureOptions = {}) {
    this.#callbacks = callbacks;
    this.#options = options;
  }

  get isActive(): boolean {
    return this.#active;
  }

  get stats(): CaptureStats | null {
    return this.#stats;
  }

  /** Smoothed input level, 0..1, for the level meter. */
  get level(): number {
    return this.#level;
  }

  static get supported(): boolean {
    return (
      typeof navigator !== "undefined" &&
      typeof navigator.mediaDevices?.getUserMedia === "function" &&
      typeof AudioWorklet !== "undefined"
    );
  }

  async start(overrides: CaptureOptions = {}): Promise<CaptureStats> {
    if (this.#active) return this.#stats ?? ({} as CaptureStats);
    if (!MicrophoneCapture.supported) {
      throw new Error("This browser cannot capture microphone audio. AudioWorklet and getUserMedia are both required.");
    }
    this.#options = { ...this.#options, ...overrides };

    // 1. Permissions first, with a message the UI can show verbatim.
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          ...(this.#options.deviceId ? { deviceId: { exact: this.#options.deviceId } } : {}),
          ...(this.#options.sampleRate ? { sampleRate: this.#options.sampleRate } : { ideal: 44_100 }),
          echoCancellation: this.#options.echoCancellation ?? true,
          noiseSuppression: this.#options.noiseSuppression ?? true,
          autoGainControl: this.#options.autoGainControl ?? true,
        },
        video: false,
      });
    } catch (error) {
      throw describeMicError(error);
    }

    const context = new AudioContext({ latencyHint: "interactive" });
    if (context.state === "suspended") await context.resume();

    const track = stream.getAudioTracks()[0];
    const settings = track?.getSettings();
    const deviceSampleRate = settings?.sampleRate ?? context.sampleRate;

    // 2. Install the worklet from a Blob URL — no separate asset, no MIME
    //    configuration, works identically in dev and in a Worker-served build.
    const blob = new Blob([WORKLET_SOURCE], { type: "application/javascript" });
    const url = URL.createObjectURL(blob);
    try {
      await context.audioWorklet.addModule(url);
    } finally {
      URL.revokeObjectURL(url);
    }

    const source = context.createMediaStreamSource(stream);
    const worklet = new AudioWorkletNode(context, "dream-tongue-capture", {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: 1,
    });

    worklet.port.onmessage = (event: MessageEvent<Float32Array>) => {
      if (!this.#active) return;
      const frame = event.data;
      this.#handleFrame(frame);
    };

    source.connect(worklet);

    this.#context = context;
    this.#stream = stream;
    this.#source = source;
    this.#worklet = worklet;
    this.#carry = new Float32Array(0);
    this.#resamplePhase = 0;
    this.#level = 0;
    this.#active = true;
    this.#stats = {
      deviceSampleRate,
      outputSampleRate: AUDIO.SAMPLE_RATE,
      ratio: deviceSampleRate / AUDIO.SAMPLE_RATE,
      deviceLabel: track?.label ?? "Default microphone",
    };

    return this.#stats;
  }

  async stop(): Promise<void> {
    if (!this.#active) return;
    this.#active = false;

    this.#worklet?.port.close();
    this.#source?.disconnect();
    this.#worklet?.disconnect();
    for (const track of this.#stream?.getTracks() ?? []) track.stop();
    await this.#context?.close().catch(() => undefined);

    this.#worklet = null;
    this.#source = null;
    this.#stream = null;
    this.#context = null;
    this.#level = 0;
    this.#callbacks.onEnded();
  }

  async setDevice(deviceId: string): Promise<void> {
    if (!this.#active) return;
    await this.stop();
    await this.start({ deviceId });
  }

  async listDevices(): Promise<MediaDeviceInfo[]> {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.enumerateDevices) return [];
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((device) => device.kind === "audioinput");
  }

  #handleFrame(frame: Float32Array): void {
    let peak = 0;
    for (let i = 0; i < frame.length; i += 1) {
      const value = frame[i] ?? 0;
      const abs = value < 0 ? -value : value;
      if (abs > peak) peak = abs;
    }
    // Fast attack, slow release: a level meter that lags is worse than useless.
    this.#level = peak > this.#level ? peak : this.#level * 0.82 + peak * 0.18;

    const ratio = this.#stats?.ratio ?? 1;
    const output =
      ratio === 1 ? frame : this.#resampleWithCarry(frame, ratio);

    if (output.length > 0) this.#callbacks.onFrame(output, this.#level);
  }

  /**
   * Linear-interpolating decimator.
   *
   * The carry keeps the fractional position between calls, which is what stops
   * a 44.1 kHz stream from drifting by ~1% over a session.
   */
  #resampleWithCarry(frame: Float32Array, ratio: number): Float32Array {
    const combined = new Float32Array(this.#carry.length + frame.length);
    combined.set(this.#carry, 0);
    combined.set(frame, this.#carry.length);

    const step = ratio;
    const outLength = Math.floor((combined.length - this.#resamplePhase) / step);
    const out = new Float32Array(Math.max(0, outLength));

    for (let i = 0; i < outLength; i += 1) {
      const position = this.#resamplePhase + i * step;
      const index = Math.floor(position);
      const frac = position - index;
      const a = combined[index] ?? 0;
      const b = combined[index + 1] ?? a;
      out[i] = a + (b - a) * frac;
    }

    const consumed = this.#resamplePhase + outLength * step;
    const keepFrom = Math.floor(consumed);
    this.#resamplePhase = consumed - keepFrom;
    this.#carry = combined.slice(keepFrom);

    return out;
  }
}

/**
 * Turns a `DOMException` from getUserMedia, or any thrown value, into a message
 * a person can act on. Also the single place a non-Error throw is normalised,
 * so callers can rely on getting a `string` back.
 */
export function describeMicError(error: unknown): string {
  const name = error instanceof Error ? error.name : "";
  const map: Record<string, string> = {
    NotAllowedError: "Microphone access was denied. Allow it in your browser's site settings, then try again.",
    PermissionDeniedError: "Microphone access was denied. Allow it in your browser's site settings, then try again.",
    NotFoundError: "No microphone was found. Connect an input device and try again.",
    DevicesNotFoundError: "No microphone was found. Connect an input device and try again.",
    NotReadableError: "The microphone is already in use by another application.",
    TrackStartError: "The microphone is already in use by another application.",
    OverconstrainedError: "No microphone matches the requested settings. Try the default device.",
    SecurityError: "Microphone capture requires a secure context. Open the app over HTTPS.",
    AbortError: "Microphone capture was interrupted. Try again.",
  };
  const message = map[name];
  if (message) return message;
  if (error instanceof Error && error.message) return error.message;
  return "The microphone could not be started.";
}
